import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createDependency, deleteDependency } from '../../store/slices/reportingSlice';
import { ChartIcon } from './FileTree';
import api from '../../services/api';

// ── Base modal wrapper ─────────────────────────────────────────────────────────

const Modal = ({ isOpen, onClose, title, children, footer, wide }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="rp-overlay">
      <div className={`rp-modal${wide ? ' wide' : ''}`} onClick={e => e.stopPropagation()}>
        <div className="rp-modal-header">
          <h3 className="rp-modal-title">{title}</h3>
          <button className="rp-modal-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
              <path d="M4.646 4.646a.5.5 0 01.708 0L8 7.293l2.646-2.647a.5.5 0 01.708.708L8.707 8l2.647 2.646a.5.5 0 01-.708.708L8 8.707l-2.646 2.647a.5.5 0 01-.708-.708L7.293 8 4.646 5.354a.5.5 0 010-.708z"/>
            </svg>
          </button>
        </div>
        <div className="rp-modal-body">{children}</div>
        {footer && <div className="rp-modal-footer">{footer}</div>}
      </div>
    </div>
  );
};

// ── New Item Modal ─────────────────────────────────────────────────────────────

export const NewItemModal = ({ isOpen, onClose, onSave, defaultType = 'doc', parentId }) => {
  const [name, setName] = useState('');
  const isFolder = defaultType === 'folder';

  useEffect(() => {
    if (isOpen) setName('');
  }, [isOpen]);

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ type: isFolder ? 'folder' : 'doc', name: name.trim(), chartType: 'gantt', parentId });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isFolder ? 'New Folder' : 'New Report'}
      footer={
        <>
          <button className="rp-btn rp-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="rp-btn rp-btn-primary" onClick={handleSave} disabled={!name.trim()}>
            Create
          </button>
        </>
      }
    >
      <div className="rp-form-group">
        <label className="rp-label">{isFolder ? 'Folder Name' : 'Report Name'}</label>
        <input
          className="rp-input"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder={isFolder ? 'e.g. Q1 Reports' : 'e.g. Project Status Overview'}
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
        />
      </div>
    </Modal>
  );
};

// ── Rename Modal ───────────────────────────────────────────────────────────────

export const RenameModal = ({ isOpen, onClose, node, onSave }) => {
  const [name, setName] = useState('');

  useEffect(() => {
    if (isOpen && node) setName(node.name || '');
  }, [isOpen, node]);

  const handleSave = () => {
    if (!name.trim() || !node) return;
    onSave(node.id, node.type, name.trim());
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Rename"
      footer={
        <>
          <button className="rp-btn rp-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="rp-btn rp-btn-primary" onClick={handleSave} disabled={!name.trim()}>
            Save
          </button>
        </>
      }
    >
      <div className="rp-form-group">
        <label className="rp-label">Name</label>
        <input
          className="rp-input"
          value={name}
          onChange={e => setName(e.target.value)}
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
        />
      </div>
    </Modal>
  );
};

// ── Add Group Modal ────────────────────────────────────────────────────────────

const GROUP_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444',
  '#f97316', '#eab308', '#22c55e', '#14b8a6',
  '#3b82f6', '#06b6d4', '#64748b', '#78716c',
];

export const AddGroupModal = ({ isOpen, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#6366f1');

  useEffect(() => {
    if (isOpen) { setName(''); setColor('#6366f1'); }
  }, [isOpen]);

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ name: name.trim(), color });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Group"
      footer={
        <>
          <button className="rp-btn rp-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="rp-btn rp-btn-primary" onClick={handleSave} disabled={!name.trim()}>
            Add Group
          </button>
        </>
      }
    >
      <div className="rp-form-group">
        <label className="rp-label">Group name</label>
        <input
          className="rp-input"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Phase 1, Sprint 3…"
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
        />
      </div>
      <div className="rp-form-group">
        <label className="rp-label">Color</label>
        <div className="rp-swatches">
          {GROUP_COLORS.map(c => (
            <button
              key={c}
              className={`rp-swatch${color === c ? ' active' : ''}`}
              style={{ background: c }}
              onClick={() => setColor(c)}
              type="button"
              aria-label={`Color ${c}`}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
};

// ── Import Modal ───────────────────────────────────────────────────────────────

const SCOPE_OPTIONS = [
  { value: 'sprints', label: 'All sprints', desc: 'Each sprint becomes a group with its tasks.' },
  { value: 'active_sprint', label: 'Active sprint only', desc: 'Import only the current active sprint.' },
  { value: 'epics', label: 'Epics', desc: 'Each epic becomes a group with its child tasks.' },
];

export const ImportModal = ({ isOpen, onClose, onImport, projects }) => {
  const [projectId, setProjectId] = useState('');
  const [scope, setScope] = useState('sprints');

  useEffect(() => {
    if (isOpen) { setProjectId(''); setScope('sprints'); }
  }, [isOpen]);

  const handleImport = () => {
    if (!projectId) return;
    onImport({ projectId: parseInt(projectId, 10), scope });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import from Project"
      footer={
        <>
          <button className="rp-btn rp-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="rp-btn rp-btn-primary" onClick={handleImport} disabled={!projectId}>
            Import
          </button>
        </>
      }
    >
      <div className="rp-form-group">
        <label className="rp-label">Project</label>
        <select
          className="rp-input"
          value={projectId}
          onChange={e => setProjectId(e.target.value)}
        >
          <option value="">Select a project…</option>
          {(projects || []).map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>
      <div className="rp-form-group">
        <label className="rp-label">Import scope</label>
        <div className="rp-radio-group">
          {SCOPE_OPTIONS.map(opt => (
            <label key={opt.value} className={`rp-radio-card${scope === opt.value ? ' active' : ''}`}>
              <input
                type="radio"
                name="scope"
                value={opt.value}
                checked={scope === opt.value}
                onChange={() => setScope(opt.value)}
              />
              <span className="rp-radio-content">
                <span className="rp-radio-label">{opt.label}</span>
                <span className="rp-radio-desc">{opt.desc}</span>
              </span>
            </label>
          ))}
        </div>
      </div>
    </Modal>
  );
};

// ── Share Document Modal ───────────────────────────────────────────────────────

export const ShareDocModal = ({ isOpen, onClose, docId, docName }) => {
  const [users, setUsers] = useState([]);
  const [shares, setShares] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !docId) return;
    setSearch('');
    setLoading(true);
    Promise.all([
      api.get('/reporting/users'),
      api.get(`/reporting/documents/${docId}/shares`),
    ]).then(([u, s]) => {
      setUsers(u.data);
      setShares(s.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [isOpen, docId]);

  useEffect(() => {
    if (isOpen && searchRef.current) setTimeout(() => searchRef.current?.focus(), 80);
  }, [isOpen]);

  const sharedIds = new Set(shares.map(s => s.user.id));

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    return !q || `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(q);
  });

  const handleAdd = async (userId) => {
    setBusy(userId);
    try {
      const res = await api.post(`/reporting/documents/${docId}/shares`, { user_id: userId, role: 'viewer' });
      setShares(prev => [...prev.filter(s => s.user.id !== userId), res.data]);
    } catch (e) {} finally { setBusy(null); }
  };

  const handleRemove = async (userId) => {
    setBusy(userId);
    try {
      await api.delete(`/reporting/documents/${docId}/shares/${userId}`);
      setShares(prev => prev.filter(s => s.user.id !== userId));
    } catch (e) {} finally { setBusy(null); }
  };

  const initials = (u) => ((u.first_name?.[0] || '') + (u.last_name?.[0] || '')).toUpperCase();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Share — ${docName || 'Report'}`} wide>
      <div className="rp-share-search">
        <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor">
          <path d="M11.742 10.344a6.5 6.5 0 10-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 001.415-1.414l-3.85-3.85a1.007 1.007 0 00-.115-.1zM12 6.5a5.5 5.5 0 11-11 0 5.5 5.5 0 0111 0z"/>
        </svg>
        <input
          ref={searchRef}
          className="rp-share-search-input"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search users…"
        />
        {search && (
          <button className="rp-share-clear" onClick={() => setSearch('')}>
            <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
              <path d="M4.646 4.646a.5.5 0 01.708 0L8 7.293l2.646-2.647a.5.5 0 01.708.708L8.707 8l2.647 2.646a.5.5 0 01-.708.708L8 8.707l-2.646 2.647a.5.5 0 01-.708-.708L7.293 8 4.646 5.354a.5.5 0 010-.708z"/>
            </svg>
          </button>
        )}
      </div>

      {loading ? (
        <div className="rp-share-loading">Loading users…</div>
      ) : (
        <div className="rp-share-user-list">
          {shares.length > 0 && (
            <>
              <div className="rp-share-section-label">Shared with</div>
              {shares.map(s => (
                <div key={s.user.id} className="rp-share-user-row shared">
                  <div className="rp-share-avatar">{initials(s.user)}</div>
                  <div className="rp-share-user-info">
                    <span className="rp-share-name">{s.user.first_name} {s.user.last_name}</span>
                    <span className="rp-share-email">{s.user.email}</span>
                  </div>
                  <button
                    className="rp-btn rp-btn-ghost rp-btn-sm"
                    disabled={busy === s.user.id}
                    onClick={() => handleRemove(s.user.id)}
                  >
                    {busy === s.user.id ? '…' : 'Remove'}
                  </button>
                </div>
              ))}
              {filtered.some(u => !sharedIds.has(u.id)) && (
                <div className="rp-share-section-label" style={{ marginTop: 'var(--space-3)' }}>Add people</div>
              )}
            </>
          )}
          {!shares.length && filtered.length === 0 && (
            <div className="rp-share-empty">{search ? 'No users match your search' : 'No users with reporting access found'}</div>
          )}
          {filtered.filter(u => !sharedIds.has(u.id)).map(u => (
            <div key={u.id} className="rp-share-user-row">
              <div className="rp-share-avatar">{initials(u)}</div>
              <div className="rp-share-user-info">
                <span className="rp-share-name">{u.first_name} {u.last_name}</span>
                <span className="rp-share-email">{u.email}</span>
              </div>
              <button
                className="rp-btn rp-btn-primary rp-btn-sm"
                disabled={busy === u.id}
                onClick={() => handleAdd(u.id)}
              >
                {busy === u.id ? '…' : 'Share'}
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="rp-modal-footer">
        <button className="rp-btn rp-btn-ghost" onClick={onClose}>Done</button>
      </div>
    </Modal>
  );
};

// ── Row Edit Modal ─────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'not-started', label: 'Not Started' },
  { value: 'on-track', label: 'On Track' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'at-risk', label: 'At Risk' },
  { value: 'behind', label: 'Behind' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'complete', label: 'Complete' },
];

const calcDaysFromDates = (startStr, endStr) => {
  if (!startStr || !endStr) return '';
  const start = new Date(startStr + 'T12:00:00');
  const end = new Date(endStr + 'T12:00:00');
  const diff = Math.round((end - start) / 86400000) + 1;
  return diff > 0 ? diff : '';
};

const addDaysToDate = (startStr, days, includeWeekends) => {
  if (!startStr || !days || parseInt(days, 10) <= 0) return null;
  const n = parseInt(days, 10);
  const start = new Date(startStr + 'T12:00:00');
  if (includeWeekends) {
    const end = new Date(start);
    end.setDate(end.getDate() + n - 1);
    return end.toISOString().split('T')[0];
  }
  // Business days only — skip Saturday (6) and Sunday (0)
  let remaining = n - 1;
  const end = new Date(start);
  while (remaining > 0) {
    end.setDate(end.getDate() + 1);
    const dow = end.getDay();
    if (dow !== 0 && dow !== 6) remaining--;
  }
  return end.toISOString().split('T')[0];
};

export const RowEditModal = ({ isOpen, onClose, row, groups, onSave, onDelete }) => {
  const [form, setForm] = useState({
    name: '',
    start_date: '',
    days: '',
    include_weekends: true,
    assignee_name: '',
    status: 'not-started',
    percent_complete: 0,
    actual_end_date: '',
  });

  useEffect(() => {
    if (isOpen && row) {
      setForm({
        name: row.name || '',
        start_date: row.start_date || '',
        days: calcDaysFromDates(row.start_date, row.end_date),
        include_weekends: true,
        assignee_name: row.custom_fields?.assignee_name || (row.assignee ? `${row.assignee.first_name || ''} ${row.assignee.last_name || ''}`.trim() : ''),
        status: row.status || 'not-started',
        percent_complete: row.percent_complete || 0,
        actual_end_date: row.actual_end_date || '',
      });
    }
  }, [isOpen, row]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = () => {
    if (!form.name.trim()) return;
    const isMilestone = !!row.is_milestone;
    const computedEndDate = isMilestone
      ? (form.start_date || null)
      : addDaysToDate(form.start_date, form.days, form.include_weekends);
    const payload = {
      name: form.name.trim(),
      is_milestone: isMilestone,
      start_date: form.start_date || null,
      end_date: computedEndDate,
      actual_end_date: (!isMilestone && form.actual_end_date) ? form.actual_end_date : null,
      assignee_id: null,
      status: form.status,
      percent_complete: isMilestone ? 0 : Math.max(0, Math.min(100, parseInt(form.percent_complete, 10) || 0)),
      custom_fields: {
        ...(row.custom_fields || {}),
        assignee_name: form.assignee_name.trim(),
      },
    };
    onSave(row.id, payload);
  };

  const dispatch = useDispatch();
  const currentDoc = useSelector(s => s.reporting.currentDoc);

  // All rows in the document (flat list, excluding this row)
  const allRows = (currentDoc?.groups || []).flatMap(g =>
    (g.rows || []).map(r => ({ ...r, groupName: g.name, groupColor: g.color }))
  ).filter(r => r.id !== row?.id);

  // Dependencies where this row is the "to" (this task depends on these)
  const myDeps = (currentDoc?.dependencies || []).filter(d => d.to_row_id === row?.id);

  const handleAddDep = async (fromRowId) => {
    if (!currentDoc || !row?.id) return;
    try {
      await dispatch(createDependency({ docId: currentDoc.id, fromRowId, toRowId: row.id })).unwrap();
    } catch (e) {
      // dependency already exists or error — silently ignore
    }
  };

  const handleRemoveDep = async (depId) => {
    try {
      await dispatch(deleteDependency({ id: depId })).unwrap();
    } catch (e) {}
  };

  if (!row) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={row.is_milestone ? 'Edit Milestone' : (row._isNew ? 'Add Task' : 'Edit Task')}
      wide
      footer={
        <>
          {!row._isNew && (
            <button className="rp-btn rp-btn-danger rp-btn-sm" onClick={() => { onDelete(row.id); onClose(); }} style={{ marginRight: 'auto' }}>
              Delete
            </button>
          )}
          <button className="rp-btn rp-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="rp-btn rp-btn-primary" onClick={handleSave} disabled={!form.name.trim()}>
            {row._isNew ? 'Create' : 'Save'}
          </button>
        </>
      }
    >
      <div className="rp-form-group">
        <label className="rp-label">Name</label>
        <input
          className="rp-input"
          value={form.name}
          onChange={e => update('name', e.target.value)}
          autoFocus
        />
      </div>

      <div className="rp-form-row-2">
        <div className="rp-form-group">
          <label className="rp-label">{row.is_milestone ? 'Date' : 'Start Date'}</label>
          <input
            className="rp-input"
            type="date"
            value={form.start_date}
            onChange={e => update('start_date', e.target.value)}
          />
        </div>
        {!row.is_milestone && (
          <div className="rp-form-group">
            <label className="rp-label">Days to Complete</label>
            <input
              className="rp-input"
              type="number"
              min="1"
              value={form.days}
              onChange={e => update('days', e.target.value)}
              placeholder="e.g. 5"
            />
          </div>
        )}
      </div>

      {!row.is_milestone && (
        <div className="rp-form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: 'var(--space-3)' }}>
          <label className="rp-label" style={{ margin: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <input
              type="checkbox"
              checked={form.include_weekends}
              onChange={e => update('include_weekends', e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            Include weekends
          </label>
          {form.start_date && form.days && parseInt(form.days, 10) > 0 && (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', marginLeft: 'auto' }}>
              Ends: {addDaysToDate(form.start_date, form.days, form.include_weekends) || '—'}
            </span>
          )}
        </div>
      )}

      {!row.is_milestone && (
        <div className="rp-form-group">
          <label className="rp-label">
            Actual End Date
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', fontWeight: 400, marginLeft: 6 }}>optional</span>
          </label>
          <input
            className="rp-input"
            type="date"
            value={form.actual_end_date}
            onChange={e => update('actual_end_date', e.target.value)}
          />
          {form.actual_end_date && form.start_date && (() => {
            const planned = addDaysToDate(form.start_date, form.days, form.include_weekends);
            if (!planned || form.actual_end_date <= planned) return null;
            const delay = Math.round((new Date(form.actual_end_date + 'T12:00:00') - new Date(planned + 'T12:00:00')) / 86400000);
            return (
              <span style={{ fontSize: 'var(--text-xs)', color: '#dc2626', marginTop: 4, display: 'block' }}>
                ⚠ {delay}d late — dependent tasks will shift
              </span>
            );
          })()}
        </div>
      )}

      <div className="rp-form-group">
        <label className="rp-label">Assignee</label>
        <input
          className="rp-input"
          value={form.assignee_name}
          onChange={e => setForm(f => ({ ...f, assignee_name: e.target.value }))}
          placeholder="Enter assignee name…"
        />
      </div>

      <div className="rp-form-group">
        <label className="rp-label">Status</label>
        <select
          className="rp-input"
          value={form.status}
          onChange={e => update('status', e.target.value)}
        >
          {STATUS_OPTIONS.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {!row.is_milestone && (
        <div className="rp-form-group">
          <label className="rp-label">% Complete: {form.percent_complete}%</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={form.percent_complete}
              onChange={e => update('percent_complete', parseInt(e.target.value, 10))}
              style={{ flex: 1 }}
            />
            <input
              className="rp-input"
              type="number"
              min="0"
              max="100"
              value={form.percent_complete}
              onChange={e => update('percent_complete', parseInt(e.target.value, 10) || 0)}
              style={{ width: 70 }}
            />
          </div>
        </div>
      )}

      {!row._isNew && (
        <div className="rp-form-group">
          <label className="rp-label">Depends On
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', fontWeight: 400, marginLeft: 6 }}>predecessors</span>
          </label>

          {/* Existing dependencies */}
          {myDeps.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {myDeps.map(dep => {
                const predRow = allRows.find(r => r.id === dep.from_row_id);
                if (!predRow) return null;
                return (
                  <span key={dep.id} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)',
                    borderRadius: 4, padding: '2px 8px', fontSize: 'var(--text-xs)'
                  }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: predRow.groupColor, flexShrink: 0 }} />
                    {predRow.name}
                    <button
                      type="button"
                      onClick={() => handleRemoveDep(dep.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-tertiary)', padding: '0 2px', fontSize: 12, lineHeight: 1 }}
                    >×</button>
                  </span>
                );
              })}
            </div>
          )}

          {/* Add predecessor dropdown */}
          <select
            className="rp-input"
            value=""
            onChange={e => { if (e.target.value) handleAddDep(parseInt(e.target.value, 10)); }}
          >
            <option value="">+ Add predecessor…</option>
            {(currentDoc?.groups || []).map(g => {
              const availableRows = (g.rows || []).filter(r =>
                r.id !== row?.id && !myDeps.some(d => d.from_row_id === r.id)
              );
              if (!availableRows.length) return null;
              return (
                <optgroup key={g.id} label={g.name}>
                  {availableRows.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </optgroup>
              );
            })}
          </select>
        </div>
      )}

    </Modal>
  );
};
